import { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from '../../services/api-axios';
import styles from './JoinByInvite.module.css';

/**
 * Landing page for a shared invite link (/join/:inviteCode).
 *
 * Signs the visitor in as a guest if they have no session, then hands off to /home.
 * It deliberately does NOT join the lobby itself: joining requires a live STOMP
 * subscription, and the websocket is only established by WsContainer once /home
 * mounts. Doing the join here would subscribe before the socket exists. The code is
 * therefore passed through router state and completed by Lobby.
 */
const JoinByInvite = () => {
    const { inviteCode } = useParams();
    const navigate = useNavigate();
    const [error, setError] = useState(null);
    const [lobbyName, setLobbyName] = useState(null);
    // React 18 StrictMode mounts effects twice in development; without this guard
    // that would create two guest accounts for one visitor.
    const started = useRef(false);

    useEffect(() => {
        if (started.current) return;
        started.current = true;

        // A dead link is the only failure that is genuinely the visitor's dead end.
        // Anything else on the preflight most likely means the stored token is
        // unusable, so it is worth one retry on a fresh session rather than
        // classifying by status code, which has varied across token shapes.
        const isDeadLink = (err) => [404, 409].includes(err?.response?.status);

        const startGuestSession = async () => {
            const res = await axios.post('/api/auth/guest', {});
            localStorage.setItem('SavedToken', 'Bearer ' + res.data['jwt-token']);
        };

        const run = async () => {
            try {
                if (!localStorage.getItem('SavedToken')) {
                    await startGuestSession();
                }

                // Fails fast on a dead or full link, so the visitor gets a real message
                // instead of silently landing in the lobby list.
                let info;
                try {
                    info = await axios.get(`/rest/lobby/invite/${inviteCode}`);
                } catch (err) {
                    if (isDeadLink(err)) throw err;
                    // Anyone who used the app before a secret rotation, or whose token
                    // simply expired, arrives holding a token the server rejects.
                    // Replace it rather than stranding them on an error page.
                    localStorage.removeItem('SavedToken');
                    await startGuestSession();
                    info = await axios.get(`/rest/lobby/invite/${inviteCode}`);
                }
                setLobbyName(info.data.lobbyName);
                if (!info.data.joinable) {
                    setError(`"${info.data.lobbyName}" is already full (${info.data.participantCount}/${info.data.maxParticipants}).`);
                    return;
                }

                navigate('/home', { replace: true, state: { pendingInviteCode: inviteCode } });
            } catch (err) {
                const status = err?.response?.status;
                setError(
                    status === 404
                        ? 'This invite link is no longer valid.'
                        : 'Could not join that lobby. Please ask for a new link.'
                );
            }
        };

        run();
    }, [inviteCode, navigate]);

    return (
        <div className={styles.container}>
            <div className={styles.card}>
                {error ? (
                    <>
                        <div className={styles.icon}>🚫</div>
                        <h2 className={styles.title}>Can't join</h2>
                        <p className={styles.message}>{error}</p>
                        <button className={styles.button} onClick={() => navigate('/login', { replace: true })}>
                            Go to ZNK
                        </button>
                    </>
                ) : (
                    <>
                        <div className={styles.spinner} />
                        <h2 className={styles.title}>Joining {lobbyName ? `"${lobbyName}"` : 'lobby'}…</h2>
                        <p className={styles.message}>Setting you up as a guest.</p>
                    </>
                )}
            </div>
        </div>
    );
};

export default JoinByInvite;
